// src/components/DossierSharesManager.jsx
// Phase 3 (§6/§17) — le collectionneur gère ses partages institutionnels :
// liste, statut (actif/expiré/révoqué), nombre de consultations (audit trail),
// copier le lien, et RÉVOQUER. RLS : le owner ne voit que SES partages et leurs logs.
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";

const RECIPIENT = { insurer: "Assureur", notaire: "Notaire", bank: "Banque", expert: "Expert", family_office: "Family Office", other: "Destinataire" };
const DOSSIER = { full: "Complet", succession: "Succession", insurance: "Assurance", art_asset: "Œuvre" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

function statusOf(s) {
  if (s.revoked_at) return { key: "revoked", label: "Révoqué", cls: "bg-gray-100 text-gray-600" };
  if (new Date(s.expires_at) <= new Date()) return { key: "expired", label: "Expiré", cls: "bg-amber-100 text-amber-700" };
  return { key: "active", label: "Actif", cls: "bg-emerald-100 text-emerald-700" };
}

export default function DossierSharesManager() {
  const { user } = useAuth();
  const [shares, setShares] = useState([]);
  const [names, setNames] = useState({});   // scope_id -> nom collection
  const [counts, setCounts] = useState({});  // share_id -> { n, last }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    const { data: rows } = await supabase
      .from("dossier_shares").select("*").eq("client_id", user.id).order("created_at", { ascending: false });
    const list = rows || [];
    setShares(list);

    // noms de collection
    const colIds = [...new Set(list.filter((s) => s.scope_type === "collection").map((s) => s.scope_id))];
    if (colIds.length) {
      const { data: cols } = await supabase.from("collections").select("id, title").in("id", colIds);
      setNames(Object.fromEntries((cols || []).map((c) => [c.id, c.title])));
    }

    // consultations (audit)
    const ids = list.map((s) => s.id);
    if (ids.length) {
      const { data: logs } = await supabase.from("dossier_share_access_log").select("share_id, accessed_at").in("share_id", ids);
      const agg = {};
      for (const l of logs || []) {
        const c = agg[l.share_id] || { n: 0, last: null };
        c.n += 1;
        if (!c.last || new Date(l.accessed_at) > new Date(c.last)) c.last = l.accessed_at;
        agg[l.share_id] = c;
      }
      setCounts(agg);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!window.confirm("Révoquer ce partage ? Le lien cessera immédiatement de fonctionner.")) return;
    setBusy(id);
    await supabase.from("dossier_shares").update({ revoked_at: new Date().toISOString() }).eq("id", id);
    setBusy("");
    load();
  };

  const copy = (token) => navigator.clipboard.writeText(`${window.location.origin}/dossier/${token}`);

  if (loading || shares.length === 0) return null; // n'apparaît que si l'utilisateur a des partages

  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">Accès contrôlé</p>
        <h2 className="mt-1 font-serif text-xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
          Mes partages de dossiers
        </h2>
        <p className="mt-1 text-sm text-gray-500">Liens institutionnels temporaires — consultations journalisées, révocables à tout moment.</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-3">Destinataire</th>
              <th className="py-2 px-3">Dossier</th>
              <th className="py-2 px-3">Périmètre</th>
              <th className="py-2 px-3">Expire</th>
              <th className="py-2 px-3 text-right">Consultations</th>
              <th className="py-2 px-3">Statut</th>
              <th className="py-2 pl-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {shares.map((s) => {
              const st = statusOf(s);
              const c = counts[s.id] || { n: 0, last: null };
              return (
                <tr key={s.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-2.5 pr-3 font-medium text-gray-900">{s.recipient_label || RECIPIENT[s.recipient_kind] || "—"}</td>
                  <td className="py-2.5 px-3 text-gray-700">{DOSSIER[s.dossier_type] || s.dossier_type}</td>
                  <td className="py-2.5 px-3 text-gray-700">{s.scope_type === "collection" ? (names[s.scope_id] || "Collection") : "Œuvre"}</td>
                  <td className="py-2.5 px-3 text-gray-600">{fmt(s.expires_at)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-700">
                    {c.n}{c.last ? <span className="text-gray-400"> · {fmt(c.last)}</span> : ""}
                  </td>
                  <td className="py-2.5 px-3"><span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                  <td className="py-2.5 pl-3 text-right">
                    {st.key === "active" ? (
                      <div className="inline-flex gap-2">
                        <button onClick={() => copy(s.token)} className="text-xs text-gray-600 underline hover:text-black">Copier</button>
                        <button onClick={() => revoke(s.id)} disabled={busy === s.id} className="text-xs font-medium text-rose-700 underline hover:text-rose-900 disabled:opacity-50">
                          {busy === s.id ? "…" : "Révoquer"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
